import {motion} from 'framer-motion';
import styled, {css, keyframes} from 'styled-components';
import {
  avatarBackgroundColor,
  avatarForegroundColor,
} from '@ui/style/avatar-colors';
import {goalStatusColors} from '@ui/style/goal-status';
import {getColorFromTheme} from '@ui/ui-utils';

export const Page = styled.div`
  color: ${getColorFromTheme('blackTwo')};
`;

export const PageHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  p {
    max-width: 800px;
    margin: 4px 0 0;
    color: ${getColorFromTheme('textBody')};
    font-size: 16px;
    line-height: 24px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PrimaryButton = styled.button`
  min-height: 46px;
  padding: 11px 24px;
  border: 1px solid ${getColorFromTheme('purple300')};
  border-radius: 10px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;

  &:hover {
    background: #494bc7;
  }

  &:focus-visible {
    outline: 3px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('borderLight')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const Panel = styled.section`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
`;

export const EmptyPanel = styled(Panel)`
  display: flex;
  min-height: 500px;
  align-items: center;
  justify-content: center;
  padding: 56px 24px;
`;

export const SummaryPanel = styled(Panel)`
  display: grid;
  grid-template-columns: 200px minmax(300px, 1fr) minmax(380px, auto);
  align-items: stretch;
  margin-bottom: 24px;
  overflow: hidden;

  @media (max-width: 1060px) {
    grid-template-columns: 200px 1fr;
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

export const SummaryProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-right: 1px solid ${getColorFromTheme('borderLight')};

  @media (max-width: 680px) {
    border-right: 0;
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  }
`;

const fillProgressRing = keyframes`
  from {
    stroke-dashoffset: 100;
  }
`;

export const ProgressRingContainer = styled.div`
  position: relative;
  display: grid;
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  place-items: center;

  svg {
    position: absolute;
    inset: 0;
    width: 64px;
    height: 64px;
    transform: rotate(-90deg);
  }

  circle {
    fill: none;
    stroke-width: 6;
  }

  circle:first-child {
    stroke: ${getColorFromTheme('borderLight')};
  }

  circle:last-child {
    animation: ${fillProgressRing} 900ms cubic-bezier(0.22, 1, 0.36, 1) both;
    stroke: ${getColorFromTheme('purple300')};
    stroke-dasharray: 100;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  span {
    position: relative;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 15px;
    font-weight: 600;
  }
`;

export const SummaryLabel = styled.div`
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;

  strong {
    display: block;
    margin-top: 3px;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }
`;

export const StatusSummary = styled.div`
  padding: 20px;
  border-right: 1px solid ${getColorFromTheme('borderLight')};

  h2 {
    margin: 0 0 14px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  @media (max-width: 1060px) {
    border-right: 0;
  }

  @media (max-width: 680px) {
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  }
`;

export const StatusBar = styled.div`
  display: flex;
  height: 5px;
  margin-bottom: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: ${getColorFromTheme('borderLight')};
`;

export const StatusSegment = styled.span<{$color: string; $width: number}>`
  width: ${({$width}) => $width}%;
  min-width: ${({$width}) => ($width > 0 ? '3px' : '0')};
  background: ${({$color}) => $color};
`;

export const StatusLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  strong {
    margin-left: 2px;
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 600;
  }

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
`;

export const StatusReviewNotice = styled.div<{$active: boolean}>`
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding: 6px 8px 6px 10px;
  border-radius: 8px;
  background: ${({theme}) => theme.colors.purple100};
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;

  > div {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
  }

  > div > svg {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    color: ${getColorFromTheme('purple300')};
  }

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 600;
  }
`;

export const StatusReviewAction = styled.button`
  display: inline-flex;
  min-height: 24px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  border-radius: 5px;
  color: ${getColorFromTheme('purple300')};
  font: 600 12px/18px Graphik, sans-serif;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    outline: none;
    background: ${getColorFromTheme('purple200')};
  }

  &:disabled {
    color: #7778c9;
    cursor: default;
  }
`;

export const DueSoon = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 13px;
  color: #b54708;
  font-size: 13px;
  font-weight: 500;
`;

export const OverdueCarryover = styled(DueSoon)`
  color: ${getColorFromTheme('red400')};
`;

export const LifecycleTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(72px, 1fr));
  align-items: center;
  gap: 6px;
  padding: 18px 20px;

  @media (max-width: 1060px) {
    grid-column: 1 / -1;
    border-top: 1px solid ${getColorFromTheme('borderLight')};
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const LifecycleButton = styled.button<{$active: boolean}>`
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 6px;
  border: 1px solid
    ${({$active, theme}) =>
      $active ? theme.colors.purple300 : theme.colors.borderLight};
  border-radius: 8px;
  background: ${({$active, theme}) =>
    $active ? theme.colors.purple100 : theme.colors.white};
  color: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.textBody};
  font-size: 12px;
  font-weight: ${({$active}) => ($active ? 600 : 500)};
  line-height: 20px;
  white-space: nowrap;

  span {
    min-width: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${({$active, theme}) =>
      $active ? theme.colors.white : theme.colors.backgroundDark};
    color: ${getColorFromTheme('textBody')};
    font-size: 10px;
    line-height: 18px;
  }
`;

export const ToolbarPanel = styled.section`
  position: relative;
  margin-bottom: 24px;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  > div:last-child {
    width: 420px;
    justify-content: flex-end;
  }

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: stretch;

    > div:last-child {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

export const ToolbarGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const ToolbarSearch = styled.label`
  display: flex;
  width: 240px;
  height: 40px;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 2px ${getColorFromTheme('purple200')};
  }

  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${getColorFromTheme('blackTwo')};
    font: 400 13px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textMuted')};
    }

    &::-webkit-search-cancel-button {
      display: none;
      appearance: none;
    }
  }

  button {
    display: grid;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    place-items: center;
    border-radius: 4px;
    color: ${getColorFromTheme('textBody')};

    &:hover,
    &:focus-visible {
      outline: none;
      background: ${getColorFromTheme('purple100')};
      color: ${getColorFromTheme('purple300')};
    }
  }

  @media (max-width: 780px) {
    width: min(100%, 320px);
  }
`;

export const ToolbarDivider = styled.span`
  width: 1px;
  height: 24px;
  margin: 0 6px;
  background: ${getColorFromTheme('borderLight')};
`;

export const ControlButton = styled.button<{$active?: boolean}>`
  display: inline-flex;
  height: 40px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid
    ${({$active, theme}) =>
      $active ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${({$active, theme}) =>
    $active ? theme.colors.purple100 : theme.colors.white};
  color: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.textBody};
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
    color: ${getColorFromTheme('purple300')};
  }

  strong {
    color: ${getColorFromTheme('purple300')};
    font-weight: 600;
  }
`;

export const FilterCount = styled.span`
  display: grid;
  min-width: 18px;
  height: 18px;
  place-items: center;
  border-radius: 999px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 11px;
  font-weight: 600;
`;

export const ToolbarMeta = styled.div`
  display: flex;
  min-height: 34px;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;
  line-height: 20px;

  strong {
    color: ${getColorFromTheme('textBody')};
    font-weight: 600;
  }
`;

export const AppliedFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
`;

export const FilterChip = styled.button`
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  gap: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${getColorFromTheme('purple100')};
  color: ${getColorFromTheme('purple300')};
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('purple200')};
  }
`;

export const Popover = styled.div<{$align?: 'left' | 'right'}>`
  position: absolute;
  top: calc(100% + 8px);
  ${({$align}) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 20;
  width: 340px;
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 12px 32px rgba(20, 20, 43, 0.12);

  @media (max-width: 520px) {
    position: fixed;
    right: 16px;
    bottom: 16px;
    left: 16px;
    top: auto;
    width: auto;
  }
`;

export const ControlWrap = styled.div`
  position: relative;
`;

export const PopoverBody = styled.div`
  padding: 0 20px 16px;
`;

export const PopoverHeader = styled.div`
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  margin: 0 -20px 16px;
  padding: 10px 14px;
  border-bottom: 1px solid ${getColorFromTheme('borderDark')};

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/22px Graphik, sans-serif;
  }
`;

export const TextButton = styled.button<{
  $active?: boolean;
  $danger?: boolean;
}>`
  padding: 2px;
  color: ${({$active, $danger}) =>
    $danger
      ? '#d52a2a'
      : $active
      ? getColorFromTheme('purple300')
      : getColorFromTheme('textBody')};
  font: 500 14px/22px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${({$danger}) =>
      $danger ? '#d52a2a' : getColorFromTheme('purple300')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
`;

export const FilterSection = styled.div`
  padding: 14px 0;
  border-top: 1px solid ${getColorFromTheme('borderLight')};

  &:first-of-type {
    padding-top: 0;
    border-top: 0;
  }

  h3 {
    margin: 0 0 8px;
    color: ${getColorFromTheme('textBody')};
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    text-transform: uppercase;
  }
`;

export const CheckOption = styled.label`
  display: flex;
  min-height: 38px;
  cursor: pointer;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 7px;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const PopoverFooter = styled.div`
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-top: 1px solid ${getColorFromTheme('borderDark')};
  background: ${getColorFromTheme('white')};

  ${TextButton} {
    min-height: 38px;
    padding: 8px 4px;
    font: 500 12px/20px Graphik, sans-serif;
  }
`;

export const SmallPrimaryButton = styled(PrimaryButton)`
  min-height: 38px;
  padding: 8px 16px;
  border-radius: 8px;
  font: 600 12px/20px Graphik, sans-serif;
`;

export const FilterPopover = styled(Popover)`
  width: 288px;
  max-height: min(540px, calc(100vh - 96px));
  border-color: ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  box-shadow: ${({theme}) => theme.elevation.level2};

  ${PopoverBody} {
    max-height: 480px;
    overflow-y: auto;
    padding: 0 16px;
  }

  ${PopoverHeader} {
    margin: 0 -16px 10px;
    border-bottom: 1px solid ${getColorFromTheme('borderDark')};
  }

  ${PopoverHeader} + ${FilterSection} {
    padding-top: 0;
    border-top: 0;
  }

  ${FilterSection} {
    padding: 10px 0;

    &:first-of-type {
      padding-top: 10px;
    }

    h3 {
      margin-bottom: 4px;
      font-size: 11px;
      line-height: 18px;
    }
  }

  ${CheckOption} {
    min-height: 32px;
    gap: 8px;
    padding: 4px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 20px;
  }

  ${PopoverFooter} {
    padding-right: 14px;
    padding-left: 14px;
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const DatePresetGrid = styled(FieldGrid)`
  grid-template-columns: 120px minmax(0, 1fr);

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const DatePopover = styled(Popover)`
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};

  ${PopoverFooter} {
    border-radius: 0 0 7px 7px;
  }
`;

export const DateSelectField = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/18px Graphik, sans-serif;
`;

export const DateSelectButton = styled.button<{$open: boolean}>`
  display: flex;
  width: 100%;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid
    ${({$open}) =>
      $open ? getColorFromTheme('purple300') : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  transition: border-color 200ms ease, box-shadow 200ms ease;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: 0 0 auto;
    color: ${getColorFromTheme('textBody')};
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
    transition: transform 160ms ease;
  }

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }
`;

export const DateInputButton = styled(DateSelectButton)`
  svg {
    transform: none;
  }
`;

export const DateCalendarMenu = styled.div<{$align?: 'left' | 'right'}>`
  position: absolute;
  top: calc(100% + 6px);
  ${({$align}) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 40;
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const DateSelectMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  width: 100%;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const DateSelectOption = styled.button<{$active: boolean}>`
  display: flex;
  width: 100%;
  min-height: 36px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;

export const SortMenu = styled(Popover)`
  width: 230px;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const SortOption = styled.button<{$active?: boolean}>`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;

export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const GoalCard = styled.article<{
  $compact?: boolean;
  $highlighted?: boolean;
}>`
  overflow: visible;
  border: 1px solid
    ${({$highlighted, theme}) =>
      $highlighted ? theme.colors.purple300 : theme.colors.borderLight};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({$highlighted, theme}) =>
    $highlighted ? `0 0 0 2px ${theme.colors.purple200}` : 'none'};
`;

export const GoalCardHeader = styled.div<{$compact?: boolean}>`
  display: grid;
  grid-template-columns: ${({$compact}) =>
    $compact
      ? 'minmax(0, 1fr) clamp(48px, 10%, 96px) minmax(328px, auto)'
      : 'minmax(0, 455px) clamp(56px, 12.2%, 130px) minmax(347px, 1fr)'};
  min-height: ${({$compact}) => ($compact ? '64px' : '88px')};
  align-items: center;
  padding: ${({$compact}) => ($compact ? '10px 12px' : '16px')};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: -2px;
  }

  @media (max-width: 840px) {
    grid-template-columns: minmax(0, 1fr) auto;

    > :nth-child(2) {
      display: none;
    }

    > :last-child {
      grid-column: 1 / -1;
      margin-top: 14px;
      padding-left: ${({$compact}) => ($compact ? '0' : '32px')};
      justify-content: flex-start;
    }
  }
`;

export const GoalPrimary = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
`;

export const DisclosureButton = styled.button<{$expanded: boolean}>`
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  border-radius: 6px;
  color: ${getColorFromTheme('blackTwo')};

  svg {
    transform: rotate(${({$expanded}) => ($expanded ? '0deg' : '-90deg')});
    transition: transform 160ms ease;
  }

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const DisclosureSpacer = styled.span`
  width: 28px;
  height: 18px;
  flex: 0 0 28px;
`;

export const GoalIdentity = styled.div`
  min-width: 0;
  text-align: left;
`;

export const GoalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 3px;
`;

export const GoalTag = styled.span<{$kpi: boolean}>`
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${({$kpi}) => ($kpi ? '#944c0c' : '#1070ca')};
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
`;

export const GoalLifecycleTag = styled.span`
  padding: 4px 7px;
  border-radius: 6px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
`;

export const GoalTitle = styled.h2`
  min-width: 0;
  flex: 1;
  margin: 0;
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const GoalMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 20px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
`;

const engagementColors = {
  neutral: {background: 'transparent', color: '#5f5f8c'},
  unread: {background: '#f6f6fe', color: '#585adf'},
  warning: {background: '#fef8f2', color: '#944c0c'},
  danger: {background: '#fef6f6', color: '#d52a2a'},
  success: {background: '#f0faf6', color: '#1a9e68'},
};

export const CardEngagementButton = styled.button<{
  $tone: keyof typeof engagementColors;
}>`
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  gap: 4px;
  margin: -2px -4px;
  padding: 2px 4px;
  border-radius: 6px;
  background: ${({$tone}) => engagementColors[$tone].background};
  color: ${({$tone}) => engagementColors[$tone].color};
  font: 400 12px/20px Graphik, sans-serif;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    stroke-width: 1.75;
  }

  &:hover {
    background: ${({$tone}) =>
      $tone === 'neutral'
        ? getColorFromTheme('backgroundDark')
        : engagementColors[$tone].background};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 1px;
  }
`;

export const EngagementIcon = styled.span`
  position: relative;
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
`;

export const UnreadIndicator = styled.i`
  position: absolute;
  top: -2px;
  right: -3px;
  width: 6px;
  height: 6px;
  border: 1px solid ${getColorFromTheme('white')};
  border-radius: 50%;
  background: #585adf;
`;

export const GoalCardEnd = styled.div<{$compact?: boolean}>`
  display: grid;
  width: 100%;
  grid-template-columns: ${({$compact}) =>
    $compact ? 'minmax(184px, 1fr) 88px 24px' : '221px 88px 24px'};
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: ${({$compact}) => ($compact ? '8px' : '12px')};

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr) 24px;
    justify-content: stretch;

    > :nth-child(2) {
      grid-column: 1;
      grid-row: 2;
      justify-self: start;
    }

    > :nth-child(3) {
      grid-column: 2;
      grid-row: 1;
    }
  }
`;

export const OwnerCell = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

export const AvatarStack = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: -8px;
  }
`;

export const InitialAvatar = styled.div<{
  $image?: string;
  $avatarHue?: number;
}>`
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  place-items: center;
  overflow: hidden;
  border: 2px solid ${getColorFromTheme('white')};
  border-radius: 50%;
  background: ${({$image, $avatarHue, theme}) =>
    $image
      ? `url(${$image}) center/cover`
      : $avatarHue === undefined
      ? theme.colors.backgroundDark
      : avatarBackgroundColor($avatarHue, theme.avatarTone)};
  color: ${({$avatarHue, theme}) =>
    $avatarHue === undefined
      ? theme.colors.blackTwo
      : avatarForegroundColor($avatarHue, theme.avatarTone)};
  font-size: 10px;
  font-weight: 600;
`;

export const GoalStatusBox = styled.div<{
  $tone?: 'danger' | 'success';
  $compact?: boolean;
}>`
  display: grid;
  width: 100%;
  height: 36px;
  grid-template-columns: ${({$compact}) =>
    $compact
      ? 'max-content minmax(68px, 84px)'
      : 'max-content minmax(76px, 104px)'};
  align-items: center;
  justify-content: space-between;
  column-gap: ${({$compact}) => ($compact ? '8px' : '0')};
  padding: 6px 12px;
  border: 1px solid
    ${({$tone, theme}) =>
      $tone === 'danger' ? theme.colors.red300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${({$tone, theme}) =>
    $tone === 'danger' ? theme.colors.red400 : theme.colors.textBody};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

export const GoalProgressValue = styled.div<{$compact?: boolean}>`
  display: grid;
  width: 100%;
  grid-template-columns: ${({$compact}) =>
    $compact ? 'minmax(38px, 54px) 30px' : 'minmax(46px, 74px) 30px'};
  align-items: center;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 10px;
  font-weight: 400;
`;

export const GoalProgressPercent = styled.span`
  width: 30px;
  flex: 0 0 30px;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 10px;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  white-space: nowrap;
`;

const performanceColor = goalStatusColors;

export const TinyProgress = styled.div<{
  $progress: number;
  $compact?: boolean;
  $goal?: boolean;
}>`
  display: block;
  width: ${({$compact}) => ($compact ? '68px' : '100%')};
  height: ${({$compact}) => ($compact ? '4px' : '8px')};
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 99px;
  background: ${getColorFromTheme('borderDark')};

  &::after {
    display: block;
    width: ${({$progress}) => $progress}%;
    height: 100%;
    border-radius: inherit;
    background: ${({$goal, theme}) =>
      $goal ? theme.colors.goalProgressNeutral : theme.colors.purple300};
    content: '';
  }
`;

const performanceTone = {
  overdue: css`
    background: ${getColorFromTheme('red100')};
    color: ${getColorFromTheme('red400')};
  `,
  on_track: css`
    background: ${getColorFromTheme('green100')};
    color: ${getColorFromTheme('green400')};
  `,
  at_risk: css`
    background: ${getColorFromTheme('orange100')};
    color: ${performanceColor.at_risk};
  `,
  behind: css`
    background: ${getColorFromTheme('red100')};
    color: ${performanceColor.behind};
  `,
  upcoming: css`
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textBody')};
  `,
  no_status: css`
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textBody')};
  `,
};

export const PerformancePill = styled.span<{
  $performance: keyof typeof performanceTone;
}>`
  padding: 6px 9px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  ${({$performance}) => performanceTone[$performance]}
`;

export const PerformanceStatus = styled.button<{
  $performance: keyof typeof performanceTone;
}>`
  display: inline-flex;
  min-width: max-content;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  margin-left: -4px;
  padding: 0 4px;
  border-radius: 6px;
  color: ${({$performance}) =>
    $performance === 'upcoming' || $performance === 'no_status'
      ? getColorFromTheme('textBody')
      : $performance === 'overdue'
      ? getColorFromTheme('red400')
      : performanceColor[$performance]};
  font-size: 12px;
  font-weight: 500;
  line-height: 28px;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const IconButton = styled.button`
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 7px;
  color: ${getColorFromTheme('textBody')};

  &:hover,
  &[aria-expanded='true'] {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('blackTwo')};
  }
`;

export const CardMenuWrap = styled.div`
  position: relative;
  width: 24px;
  flex: 0 0 24px;
`;

export const CardMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  z-index: 30;
  width: 180px;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 9px 38px rgba(0, 0, 0, 0.08);
`;

export const CardMenuButton = styled.button<{
  $danger?: boolean;
  $warning?: boolean;
}>`
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({$danger, $warning}) =>
    $danger ? '#e52e2e' : $warning ? '#d66a00' : getColorFromTheme('textBody')};
  font: 400 13px/18px Graphik, sans-serif;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${({$danger, $warning}) =>
      $danger
        ? '#e52e2e'
        : $warning
        ? '#d66a00'
        : getColorFromTheme('blackTwo')};
    font-weight: 500;
  }
`;

export const KeyResults = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
  padding: 12px 50px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const KeyResultRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px minmax(0, 1fr);
  align-items: center;
  min-height: 44px;

  > ${OwnerCell} {
    width: 130px;
    padding: 8px 16px;
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr) auto;

    > :last-child {
      grid-column: 1 / -1;
    }
  }
`;

export const KeyResultDivider = styled.div`
  width: 100%;
  height: 1px;
  flex: 0 0 1px;
  background: ${getColorFromTheme('borderLight')};
`;

export const ResultIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;

  p {
    margin: 0;
    overflow: hidden;
    color: ${getColorFromTheme('textBody')};
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ResultValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 600;
  }
`;

export const CardFooter = styled.button`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 8px 0;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  font-weight: 400;

  strong {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: ${getColorFromTheme('purple300')};
    font-weight: 600;

    &::before {
      width: 1px;
      height: 10px;
      background: ${getColorFromTheme('borderDark')};
      content: '';
    }
  }

  &:hover {
    background: ${getColorFromTheme('purple100')};
  }
`;

export const NoResults = styled(Panel)`
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 20px;
  text-align: center;

  > svg {
    margin-bottom: 12px;
  }

  h2 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  p {
    max-width: 440px;
    margin: 0 0 12px;
    color: ${getColorFromTheme('textBody')};
    font-size: 13px;
    line-height: 20px;
  }
`;

export const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;

  div {
    display: flex;
    gap: 8px;
  }

  button {
    min-width: 38px;
    height: 38px;
    padding: 0 10px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 500;
  }

  button:disabled {
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const LoaderPanel = styled(Panel)`
  display: grid;
  min-height: 360px;
  place-items: center;
`;
